import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { take } from 'rxjs/operators';
import {
    Room,
    RoomEvent,
    Track,
    RemoteTrackPublication,
    RemoteParticipant,
    LocalTrackPublication,
    LocalParticipant,
    LocalVideoTrack,
    Participant,
    ConnectionState,
    TrackPublication,
    facingModeFromLocalTrack,
} from 'livekit-client';
import type { CallRoom, CallRoomParticipant } from '../calls-widget/calls-types';
import { CallsService } from '../calls-widget/calls.service';
import type { CallTokenResponse } from '../calls-widget/calls.service';
import { ApiService } from 'app/shared/apis/api.service';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';
import { LivekitTrackDirective } from './livekit-track.directive';

export interface CallTile {
    identity: string;
    name: string;
    initials: string;
    isLocal: boolean;
    micMuted: boolean;
    cameraOff: boolean;
    speaking: boolean;
    videoTrack: Track | null;
    audioTrack: Track | null;
}

export type CallLayoutMode = 'solo' | 'spotlight' | 'grid';

@Component({
    selector: 'app-calls-in-call',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        DragDropModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
        LivekitTrackDirective,
    ],
    templateUrl: './calls-in-call.component.html',
    styleUrls: ['./calls-in-call.component.scss'],
})
export class CallsInCallComponent implements OnInit, OnDestroy {
    @Input() room: CallRoom | null = null;
    @Input() participants: CallRoomParticipant[] = [];
    @Input() chatOpen = false;
    @Output() leave = new EventEmitter<void>();
    @Output() openChat = new EventEmitter<void>();
    @Output() fullscreenChange = new EventEmitter<boolean>();

    isFullscreen = false;
    panelWidth = 400;
    panelHeight = 480;
    connecting = true;
    connectError: string | null = null;
    micEnabled = true;
    cameraEnabled = false;
    switchingCamera = false;
    /** Entradas de vídeo disponíveis (atualizado após conectar / trocar dispositivo). */
    videoInputDevices: MediaDeviceInfo[] = [];

    tiles: CallTile[] = [];

    private livekitRoom: Room | null = null;
    private leaving = false;

    constructor(
        private readonly callsService: CallsService,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    get layoutMode(): CallLayoutMode {
        if (this.tiles.length <= 1) return 'solo';
        if (this.tiles.length === 2) return 'spotlight';
        return 'grid';
    }

    /**
     * Colunas do grid por contagem (evita 1 linha espremida no fullscreen).
     * Painel estreito: no máx. 2–3 cols. Fullscreen: 2 / 3 / 4 / √n.
     */
    get gridColumnCount(): number {
        const n = this.tiles.length;
        if (n <= 1) return 1;

        if (!this.isFullscreen) {
            if (n <= 6) return 2;
            return 3;
        }

        if (n <= 4) return 2;
        if (n <= 9) return 3;
        if (n <= 16) return 4;
        return Math.ceil(Math.sqrt(n));
    }

    /** Densidade visual: avatares/labels menores com muitos participantes. */
    get gridDensity(): 'comfortable' | 'compact' | 'dense' {
        const n = this.tiles.length;
        if (n >= 10) return 'dense';
        if (n >= 6) return 'compact';
        return 'comfortable';
    }

    /** Remoto em destaque quando há 2 participantes. */
    get spotlightTile(): CallTile | null {
        if (this.layoutMode !== 'spotlight') return null;
        return this.tiles.find((t) => !t.isLocal) ?? this.tiles[0] ?? null;
    }

    /** Local em miniatura (PiP) quando há 2 participantes. */
    get pipTile(): CallTile | null {
        if (this.layoutMode !== 'spotlight') return null;
        return this.tiles.find((t) => t.isLocal) ?? null;
    }

    get soloTile(): CallTile | null {
        if (this.layoutMode !== 'solo') return null;
        return this.tiles[0] ?? null;
    }

    /** Várias webcams ou celular (frente/trás). */
    get canSwitchCamera(): boolean {
        if (this.videoInputDevices.length >= 2) return true;
        return this.videoInputDevices.length >= 1 && this.isMobileUserAgent();
    }

    ngOnInit(): void {
        if (this.room) {
            this.initializeLiveKit(this.room);
        }
    }

    ngOnDestroy(): void {
        this.disposeLiveKit();
    }

    private initializeLiveKit(callRoom: CallRoom): void {
        if (!callRoom?.id) return;

        ApiService.userCurrent.pipe(take(1)).subscribe((user) => {
            this.callsService.getCallToken(callRoom.slug ?? callRoom.id).subscribe({
                next: async (resp: CallTokenResponse) => {
                    try {
                        await this.connectLiveKit(resp);
                    } catch (e) {
                        console.error(e);
                        this.connectError = 'Não foi possível conectar à chamada.';
                        this.connecting = false;
                        this.cdr.markForCheck();
                    }
                },
                error: (err) => {
                    console.error('Erro ao obter token da chamada', err);
                    this.connectError = err?.error?.message || 'Erro ao obter token da chamada.';
                    this.connecting = false;
                    this.cdr.markForCheck();
                },
            });
        });
    }

    private async connectLiveKit(resp: CallTokenResponse): Promise<void> {
        this.disposeLiveKit();
        this.connecting = true;
        this.connectError = null;
        this.tiles = [];
        this.cdr.markForCheck();

        const room = new Room({
            adaptiveStream: true,
            dynacast: true,
        });
        this.livekitRoom = room;

        room.on(RoomEvent.ParticipantConnected, (participant) => {
            this.upsertTileFromParticipant(participant);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            this.removeTile(participant.identity);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
            this.applyTrack(participant, track, true);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.TrackUnsubscribed, (track, _publication, participant) => {
            this.applyTrack(participant, track, false);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.LocalTrackPublished, (publication: LocalTrackPublication) => {
            if (publication.track && room.localParticipant) {
                this.applyTrack(room.localParticipant, publication.track, true);
                this.cdr.markForCheck();
            }
        });
        room.on(RoomEvent.LocalTrackUnpublished, (publication: LocalTrackPublication) => {
            if (publication.track && room.localParticipant) {
                this.applyTrack(room.localParticipant, publication.track, false);
                this.cdr.markForCheck();
            }
        });
        room.on(RoomEvent.TrackMuted, (publication, participant) => {
            this.refreshMuteState(participant, publication);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
            this.refreshMuteState(participant, publication);
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
            const speakingIds = new Set(speakers.map((s) => s.identity));
            this.tiles = this.tiles.map((t) => ({
                ...t,
                speaking: speakingIds.has(t.identity),
            }));
            this.cdr.markForCheck();
        });
        room.on(RoomEvent.Disconnected, () => {
            if (!this.leaving) {
                this.onLeave();
            }
        });
        room.on(RoomEvent.MediaDevicesChanged, () => {
            void this.refreshVideoInputDevices();
        });

        await room.connect(resp.url, resp.token, {
            autoSubscribe: true,
        });

        // Mic ligado, câmera desligada no início
        await room.localParticipant.setMicrophoneEnabled(true);
        await room.localParticipant.setCameraEnabled(false);
        this.syncLocalControls();

        this.upsertTileFromParticipant(room.localParticipant);
        room.remoteParticipants.forEach((participant) => {
            this.upsertTileFromParticipant(participant);
            participant.trackPublications.forEach((pub: RemoteTrackPublication) => {
                if (pub.track) {
                    this.applyTrack(participant, pub.track, true);
                }
            });
        });
        room.localParticipant.trackPublications.forEach((pub) => {
            if (pub.track) {
                this.applyTrack(room.localParticipant, pub.track, true);
            }
        });

        await this.refreshVideoInputDevices();
        this.fitPanelToTileCount();

        this.connecting = false;
        this.cdr.markForCheck();
    }

    /** Amplia o painel flutuante quando há muitos participantes. */
    private fitPanelToTileCount(): void {
        if (this.isFullscreen) return;
        const n = this.tiles.length;
        if (n >= 10) {
            this.panelWidth = Math.max(this.panelWidth, 560);
            this.panelHeight = Math.max(this.panelHeight, 640);
        } else if (n >= 6) {
            this.panelWidth = Math.max(this.panelWidth, 480);
            this.panelHeight = Math.max(this.panelHeight, 560);
        }
    }

    private isMobileUserAgent(): boolean {
        if (typeof navigator === 'undefined') return false;
        return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    }

    private async refreshVideoInputDevices(): Promise<void> {
        try {
            if (!navigator.mediaDevices?.enumerateDevices) {
                this.videoInputDevices = [];
                return;
            }
            const all = await navigator.mediaDevices.enumerateDevices();
            this.videoInputDevices = all.filter((d) => d.kind === 'videoinput' && d.deviceId);
            this.cdr.markForCheck();
        } catch {
            this.videoInputDevices = [];
        }
    }

    private upsertTileFromParticipant(participant: Participant): void {
        const isLocal =
            participant instanceof LocalParticipant || participant === this.livekitRoom?.localParticipant;
        const name = participant.name || participant.identity || 'Participante';
        const existing = this.tiles.find((t) => t.identity === participant.identity);
        const cameraOff = !this.hasActiveCamera(participant);
        const micMuted = this.isMicMuted(participant);

        if (existing) {
            existing.name = name;
            existing.initials = this.initialsFromName(name);
            existing.cameraOff = cameraOff;
            existing.micMuted = micMuted;
            return;
        }

        this.tiles = [
            ...this.tiles,
            {
                identity: participant.identity,
                name,
                initials: this.initialsFromName(name),
                isLocal,
                micMuted,
                cameraOff,
                speaking: false,
                videoTrack: null,
                audioTrack: null,
            },
        ];
    }

    private applyTrack(participant: Participant, track: Track, attached: boolean): void {
        this.upsertTileFromParticipant(participant);
        const tile = this.tiles.find((t) => t.identity === participant.identity);
        if (!tile) return;

        if (track.kind === Track.Kind.Video) {
            tile.videoTrack = attached ? track : null;
            tile.cameraOff = !attached || track.isMuted || !this.hasActiveCamera(participant);
        } else if (track.kind === Track.Kind.Audio) {
            // Áudio local não anexa (eco)
            if (tile.isLocal) {
                tile.audioTrack = null;
            } else {
                tile.audioTrack = attached ? track : null;
            }
            tile.micMuted = this.isMicMuted(participant);
        }

        this.tiles = [...this.tiles];
        this.syncLocalControls();
    }

    private refreshMuteState(participant: Participant, publication: TrackPublication): void {
        const tile = this.tiles.find((t) => t.identity === participant.identity);
        if (!tile) return;

        if (publication.kind === Track.Kind.Video) {
            tile.cameraOff = publication.isMuted || !this.hasActiveCamera(participant);
            if (publication.isMuted) {
                // Mantém track para reativar, mas UI mostra avatar
                tile.cameraOff = true;
            }
        } else if (publication.kind === Track.Kind.Audio) {
            tile.micMuted = publication.isMuted || this.isMicMuted(participant);
        }

        this.tiles = [...this.tiles];
        this.syncLocalControls();
    }

    private hasActiveCamera(participant: Participant): boolean {
        for (const pub of participant.trackPublications.values()) {
            if (pub.kind === Track.Kind.Video && pub.track && !pub.isMuted) {
                return true;
            }
        }
        if (participant instanceof LocalParticipant) {
            return participant.isCameraEnabled;
        }
        return false;
    }

    private isMicMuted(participant: Participant): boolean {
        if (participant instanceof LocalParticipant) {
            return !participant.isMicrophoneEnabled;
        }
        for (const pub of participant.trackPublications.values()) {
            if (pub.kind === Track.Kind.Audio) {
                return pub.isMuted || !pub.track;
            }
        }
        // Sem publicação de áudio ainda → considera mudo visualmente só se soubermos
        return false;
    }

    private removeTile(identity: string): void {
        this.tiles = this.tiles.filter((t) => t.identity !== identity);
    }

    private syncLocalControls(): void {
        const local = this.livekitRoom?.localParticipant;
        if (!local) return;
        this.micEnabled = local.isMicrophoneEnabled;
        this.cameraEnabled = local.isCameraEnabled;
        const localTile = this.tiles.find((t) => t.isLocal);
        if (localTile) {
            localTile.micMuted = !local.isMicrophoneEnabled;
            localTile.cameraOff = !local.isCameraEnabled;
        }
    }

    private initialsFromName(name: string): string {
        const parts = (name || '').trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) return '?';
        if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }

    private disposeLiveKit(): void {
        const room = this.livekitRoom;
        this.livekitRoom = null;
        this.tiles = [];
        if (room) {
            try {
                if (room.state !== ConnectionState.Disconnected) {
                    room.disconnect(true);
                }
            } catch (e) {
                console.warn('LiveKit disconnect error', e);
            }
        }
    }

    async toggleMic(): Promise<void> {
        const local = this.livekitRoom?.localParticipant;
        if (!local) return;
        const next = !local.isMicrophoneEnabled;
        await local.setMicrophoneEnabled(next);
        this.micEnabled = next;
        this.syncLocalControls();
        this.tiles = [...this.tiles];
        this.cdr.markForCheck();
    }

    async toggleCamera(): Promise<void> {
        const local = this.livekitRoom?.localParticipant;
        if (!local) return;
        const next = !local.isCameraEnabled;
        await local.setCameraEnabled(next);
        this.cameraEnabled = next;
        this.syncLocalControls();
        this.tiles = [...this.tiles];
        this.cdr.markForCheck();
    }

    async switchCamera(): Promise<void> {
        const room = this.livekitRoom;
        const local = room?.localParticipant;
        if (!room || !local || this.switchingCamera || !this.canSwitchCamera) return;

        this.switchingCamera = true;
        this.cdr.markForCheck();
        try {
            await this.refreshVideoInputDevices();
            const devices = this.videoInputDevices;

            if (devices.length >= 2) {
                const currentId = room.getActiveDevice('videoinput') || devices[0].deviceId;
                const idx = Math.max(
                    0,
                    devices.findIndex((d) => d.deviceId === currentId),
                );
                const nextDevice = devices[(idx + 1) % devices.length];
                if (!local.isCameraEnabled) {
                    await local.setCameraEnabled(true, { deviceId: nextDevice.deviceId });
                } else {
                    await room.switchActiveDevice('videoinput', nextDevice.deviceId);
                }
            } else {
                const facingMode = this.nextFacingMode(local);
                if (!local.isCameraEnabled) {
                    await local.setCameraEnabled(true, { facingMode });
                } else {
                    const pub = local.getTrackPublication(Track.Source.Camera);
                    const track = pub?.track;
                    if (track instanceof LocalVideoTrack) {
                        await track.restartTrack({ facingMode });
                    } else {
                        await local.setCameraEnabled(false);
                        await local.setCameraEnabled(true, { facingMode });
                    }
                }
            }

            this.syncLocalControls();
            this.tiles = [...this.tiles];
        } catch (e) {
            console.warn('Não foi possível alternar a câmera', e);
        } finally {
            this.switchingCamera = false;
            this.cdr.markForCheck();
        }
    }

    private nextFacingMode(local: LocalParticipant): 'user' | 'environment' {
        const pub = local.getTrackPublication(Track.Source.Camera);
        const track = pub?.track;
        if (track instanceof LocalVideoTrack) {
            const { facingMode } = facingModeFromLocalTrack(track);
            return facingMode === 'environment' ? 'user' : 'environment';
        }
        return 'user';
    }

    onLeave(): void {
        this.leaving = true;
        this.disposeLiveKit();
        this.leave.emit();
    }

    toggleFullscreen(): void {
        this.isFullscreen = !this.isFullscreen;
        this.fullscreenChange.emit(this.isFullscreen);
    }

    onOpenChat(): void {
        if (this.isFullscreen) {
            this.openChat.emit();
        }
    }

    trackByIdentity(_index: number, tile: CallTile): string {
        return tile.identity;
    }
}
